/**
 * src/config/env.ts
 * 
 * Single source of truth for environment configuration.
 * All environment-dependent logic should import from this module.
 */

import {
  isValidAppMode,
  resolveEnvironmentMode,
  type AppMode,
  type ExecutionMode,
} from './environmentMode';

// ============================================================================
// Environment Variable Access
// ============================================================================

/**
 * Get environment variables, supporting both Vite (import.meta.env) and Jest/Node (process.env)
 */
type RuntimeEnv = Record<string, string | boolean | undefined>;

const getEnv = (): RuntimeEnv => {
  const nodeEnv = typeof process !== 'undefined' && process.env
    ? process.env as unknown as RuntimeEnv
    : {};
  // Vite statically replaces import.meta.env. The Jest transformer maps the
  // same expression to process.env, so one explicit path works in both.
  const viteEnv = typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env as unknown as RuntimeEnv
    : {};
  return { ...nodeEnv, ...viteEnv };
};

const env = getEnv();
const readStringEnv = (key: string): string => {
  const value = env[key];
  return typeof value === 'string' ? value : '';
};

// ============================================================================
// Core Environment Variables
// ============================================================================

/**
 * Demo is the local/test default. Production builds fail closed to production
 * when the variable is omitted, preventing accidental sample-data deployments.
 */
const environmentMode = resolveEnvironmentMode({
  appMode: env.VITE_APP_MODE,
  executionMode: env.VITE_EXECUTION_MODE ?? env.NEXT_PUBLIC_EXECUTION_MODE,
  viteMode: env.MODE,
  viteProduction: env.PROD,
  nodeEnv: env.NODE_ENV,
});

export const APP_MODE: AppMode = environmentMode.appMode;
export const EXECUTION_MODE: ExecutionMode = environmentMode.executionMode;
export const ENVIRONMENT_CONFIGURATION_ERROR = environmentMode.configurationError;

/**
 * Account used to seed the first browser-local session while the product is
 * running without a backend. This is deliberately a role name, not a password
 * or credential: every VITE_* value is visible in the browser bundle.
 */
export const LOCAL_BOOTSTRAP_ROLE = readStringEnv('VITE_LOCAL_BOOTSTRAP_ROLE') || 'platform_owner';

/**
 * Supabase project URL
 */
export const SUPABASE_URL = readStringEnv('VITE_SUPABASE_URL');

/** Supabase browser-safe publishable key. */
export const SUPABASE_PUBLISHABLE_KEY = readStringEnv('VITE_SUPABASE_PUBLISHABLE_KEY')
  || readStringEnv('VITE_SUPABASE_ANON_KEY')
  || '';

/** @deprecated Compatibility alias for modules still using the legacy name. */
export const SUPABASE_ANON_KEY = SUPABASE_PUBLISHABLE_KEY;

/**
 * Sentry DSN for error monitoring
 */
export const SENTRY_DSN = readStringEnv('VITE_SENTRY_DSN');
export const ENABLE_MONITORING = APP_MODE === 'production'
  || (APP_MODE === 'staging' && readStringEnv('VITE_ENABLE_MONITORING') === 'true');

// ============================================================================
// Feature Flags
// ============================================================================

/**
 * Whether MFA enrollment is required for all real users.
 * This is a deployment-level policy flag until workspace-scoped policy storage exists.
 */
export const REQUIRE_MFA = readStringEnv('VITE_REQUIRE_MFA') === 'true';

/**
 * Internal diagnostics and component-test routes. They are impossible to
 * enable in production, even when the deployment variable is misconfigured.
 */
export const ENABLE_INTERNAL_ROUTES =
  APP_MODE !== 'production'
  && readStringEnv('VITE_ENABLE_INTERNAL_ROUTES') === 'true';

/**
 * Browser E2E login helper. Like internal diagnostics, this is fail-closed in
 * production.
 */
export const ENABLE_E2E_ROUTES =
  APP_MODE !== 'production'
  && readStringEnv('VITE_ENABLE_E2E_ROUTES') === 'true';

/**
 * Governance Journey — compatibility flag for older operational panels.
 *
 * The routed governance workspace is now canonical. This flag remains only
 * while older embedded operational panels migrate their optional CTA.
 */
export const ENABLE_GOVERNANCE_JOURNEY = readStringEnv('VITE_ENABLE_GOVERNANCE_JOURNEY') === 'true';

// ============================================================================
// Derived Configuration
// ============================================================================

/**
 * Whether Supabase credentials are properly configured
 */
export const hasSupabaseCredentials = Boolean(SUPABASE_URL && SUPABASE_PUBLISHABLE_KEY);

/**
 * IS_DEMO_MODE - The single source of truth for demo mode detection
 * 
 * Demo mode is active only when APP_MODE is explicitly set to 'demo'.
 * Missing credentials no longer force demo fallback.
 */
export const IS_DEMO_MODE = APP_MODE === 'demo';

// ============================================================================
// Runtime Safety Checks
// ============================================================================

// Only run in browser environment, not during tests
if (typeof window !== 'undefined' && typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
  if (ENVIRONMENT_CONFIGURATION_ERROR) {
    console.error(`[env] ${ENVIRONMENT_CONFIGURATION_ERROR}`);
  } else if (APP_MODE !== 'demo' && !hasSupabaseCredentials) {
    console.warn(
      `[env] APP_MODE is "${APP_MODE}" but Supabase credentials are missing. ` +
      `Running without Supabase connectivity. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY to use Supabase.`
    );
  }

  // Log current mode for debugging (only in development)
  if (APP_MODE !== 'production') {
    console.info(`[env] Running in ${IS_DEMO_MODE ? 'DEMO' : APP_MODE.toUpperCase()} mode`);
    console.info('[env] VITE_SUPABASE_URL:', SUPABASE_URL || '(empty)');
    console.info('[env] VITE_SUPABASE_PUBLISHABLE_KEY present:', Boolean(SUPABASE_PUBLISHABLE_KEY));
    console.info('[env] URL includes /rest/v1:', SUPABASE_URL.includes('/rest/v1'));
    console.info('[env] Key uses the publishable format:', SUPABASE_PUBLISHABLE_KEY.startsWith('sb_publishable_'));
    console.info('[env] NOTE: Restart Vite dev server after changing .env.dev variables.');
  }
}

// ============================================================================
// Type Definitions
// ============================================================================

export { isValidAppMode };
export type { AppMode, ExecutionMode };
